// The /admin tools of the Festival Systems team, each behind the master key
const en = {
  adminTools: 'Admin tools',
  masterKey: 'Master key',
  festivalId: 'Festival ID',
  error: 'An error occurred',
  secretRequired: 'Firebase Secret Key is required',
  secretNotJson: 'Firebase Secret Key must be a valid JSON',

  homeDescription: 'For the Festival Systems team. Each tool asks for the master key.',
  backToSignIn: 'Back to sign in',
  /** Each tool's name on the admin home, which is also its page's title */
  tools: {
    createFestival: { title: 'Create festival', description: 'A festival with its first admin and subdomain.' },
    createUser: { title: 'Create user', description: 'Adds a user, with roles, to a festival.' },
    festivalsList: { title: 'Festivals list', description: 'Every festival, to open its sign-in or app preview.' },
    migrations: { title: 'Perform migrations', description: 'Runs pending migrations after an API deploy.' },
    firebaseSecret: { title: 'Update Firebase secret', description: "Replaces a festival's push credentials." },
  },

  createFestivalDescription:
    'Sets up a festival with its admin, subdomain and Firebase secret, then opens its sign-in.',
  festivalName: 'Festival name',
  adminEmail: 'Admin email',
  subdomain: 'Subdomain',
  subdomainPlaceholder: 'e.g., myfestival',
  subdomainRule: 'Lowercase letters and numbers, with hyphens between them',
  subdomainHelp: "This will be used as your festival's subdomain (e.g., my-festival.festival.systems)",
  subdomainInvalid: 'A subdomain has lowercase letters and numbers, with hyphens between them',
  secretKey: 'Firebase secret key (JSON)',
  createFestival: 'Create festival',

  createUserDescription: 'Adds a user to an existing festival, with the roles they need.',
  email: 'Email',
  roles: 'Roles',
  createUser: 'Create user',
  userCreatedTitle: 'User created',
  userCreated: 'User created successfully',

  festivalsListDescription:
    'Every festival on the platform, with the name its app shows. Open one to go to its sign-in, or share its app preview.',
  viewFestivals: 'View festivals',
  name: 'Name',
  preview: 'Preview',
  previewFestival: (name: string) => `Preview ${name} (opens in a new tab)`,

  migrationsDescription: "Runs the data migrations that haven't run yet. Do it after each API deploy.",
  migrate: 'Migrate',
  migrating: 'Migrating...',
  migrationsDone: 'Migrations completed successfully',
  migrationsFailed: 'Failed to perform migrations',

  secretDescription: "Replaces the Firebase service account a festival's push notifications are sent with.",
  loading: 'Loading…',
  continue: 'Continue',
  festival: 'Festival',
  selectFestival: 'Select a festival…',
  noFestivalSelected: 'Please select a festival',
  newSecretKey: 'New Firebase secret key (JSON)',
  secretHelp: 'Paste the full Firebase service-account JSON. It will replace the current secret.',
  review: 'Review & confirm',
  /** Around the festival's name, in bold */
  replaceWarningBefore: 'This will replace the Firebase secret for',
  replaceWarningAfter:
    '. Push notifications will fail until the new credentials are valid. This action cannot be undone from this UI.',
  /** Around the festival's name, in bold, that has to be typed as is */
  typeToConfirmBefore: 'Type ',
  typeToConfirmAfter: ' to confirm',
  updateSecret: 'Update Firebase secret',
  updating: 'Updating…',
  /** Around the festival's name, in bold */
  secretUpdatedBefore: 'Firebase secret updated successfully for',
  secretUpdatedAfter: '.',
  backToTools: 'Back to admin tools',
};

const fr: typeof en = {
  adminTools: "Outils d'administration",
  masterKey: 'Clé maître',
  festivalId: 'Identifiant du festival',
  error: 'Une erreur est survenue',
  secretRequired: 'La clé secrète Firebase est obligatoire',
  secretNotJson: 'La clé secrète Firebase doit être un JSON valide',

  homeDescription: "Pour l'équipe Festival Systems. Chaque outil demande la clé maître.",
  backToSignIn: 'Retour à la connexion',
  tools: {
    createFestival: {
      title: 'Créer un festival',
      description: 'Un festival avec son premier administrateur et son sous-domaine.',
    },
    createUser: { title: 'Créer un utilisateur', description: 'Ajoute un utilisateur à un festival, avec ses rôles.' },
    festivalsList: {
      title: 'Liste des festivals',
      description: 'Tous les festivals, pour ouvrir leur page de connexion ou un aperçu de leur application.',
    },
    migrations: {
      title: 'Lancer les migrations',
      description: "Lance les migrations en attente après un déploiement de l'API.",
    },
    firebaseSecret: {
      title: 'Mettre à jour la clé secrète Firebase',
      description: "Remplace les identifiants des notifications push d'un festival.",
    },
  },

  createFestivalDescription:
    'Crée un festival avec son administrateur, son sous-domaine et sa clé secrète Firebase, puis ouvre sa page de connexion.',
  festivalName: 'Nom du festival',
  adminEmail: "E-mail de l'administrateur",
  subdomain: 'Sous-domaine',
  subdomainPlaceholder: 'par ex. myfestival',
  subdomainRule: 'Lettres minuscules et chiffres, avec des tirets entre eux',
  subdomainHelp: 'Ce sera le sous-domaine de votre festival (par ex. my-festival.festival.systems)',
  subdomainInvalid: 'Un sous-domaine contient des lettres minuscules et des chiffres, avec des tirets entre eux',
  secretKey: 'Clé secrète Firebase (JSON)',
  createFestival: 'Créer le festival',

  createUserDescription: 'Ajoute un utilisateur à un festival existant, avec les rôles dont il a besoin.',
  email: 'E-mail',
  roles: 'Rôles',
  createUser: "Créer l'utilisateur",
  userCreatedTitle: 'Utilisateur créé',
  userCreated: "L'utilisateur a bien été créé",

  festivalsListDescription:
    "Tous les festivals de la plateforme, avec le nom qu'affiche leur application. Ouvrez-en un pour aller à sa page de connexion ou partager un aperçu de son application.",
  viewFestivals: 'Voir les festivals',
  name: 'Nom',
  preview: 'Aperçu',
  previewFestival: (name: string) => `Aperçu de ${name} (s’ouvre dans un nouvel onglet)`,

  migrationsDescription:
    "Lance les migrations de données qui n'ont pas encore été exécutées. À faire après chaque déploiement de l'API.",
  migrate: 'Migrer',
  migrating: 'Migration...',
  migrationsDone: 'Migrations terminées avec succès',
  migrationsFailed: "Impossible d'exécuter les migrations",

  secretDescription:
    "Remplace le compte de service Firebase avec lequel les notifications push d'un festival sont envoyées.",
  loading: 'Chargement…',
  continue: 'Continuer',
  festival: 'Festival',
  selectFestival: 'Sélectionnez un festival…',
  noFestivalSelected: 'Veuillez sélectionner un festival',
  newSecretKey: 'Nouvelle clé secrète Firebase (JSON)',
  secretHelp: 'Collez le JSON complet du compte de service Firebase. Il remplacera la clé secrète actuelle.',
  review: 'Vérifier et confirmer',
  replaceWarningBefore: 'Cela remplacera la clé secrète Firebase du festival',
  replaceWarningAfter:
    '. Les notifications push échoueront tant que les nouveaux identifiants ne seront pas valides. Cette action ne peut pas être annulée depuis cette interface.',
  typeToConfirmBefore: 'Saisissez ',
  typeToConfirmAfter: ' pour confirmer',
  updateSecret: 'Mettre à jour la clé secrète Firebase',
  updating: 'Mise à jour…',
  secretUpdatedBefore: 'La clé secrète Firebase a bien été mise à jour pour le festival',
  secretUpdatedAfter: '.',
  backToTools: "Retour aux outils d'administration",
};

const messages = { en, fr };
export default messages;
